import { useTranslation } from "react-i18next";
import UserProfilePictureAvatar from "../../avatar/UserProfilePictureAvatar";
import { Moment } from "moment";
import { classNames } from "../../../helpers";

export enum ScheduledRoomCardStates {
  inactive,
  active,
}

type ScheduledRoomCardBaseProps = {
  profilePictureUrl?: string;
  participantName: string;
  sessionStatusText: string;
  telawahName: string;
  readingProgress: number;
  sessionNumber: number;
  sessionStartTime: Moment;
  isEvaluationSession: boolean;
  state?: ScheduledRoomCardStates;
};

export default function ScheduledRoomCard(props: ScheduledRoomCardBaseProps) {
  const { t } = useTranslation("", { keyPrefix: "page.tutor.dashboard.room" });
  const {
    profilePictureUrl,
    participantName,
    telawahName,
    sessionStatusText,
    readingProgress,
    sessionNumber,
    sessionStartTime,
    isEvaluationSession,
    state = ScheduledRoomCardStates.inactive,
  } = props;

  const isInactive = state === ScheduledRoomCardStates.inactive;
  const bgColor = isInactive ? "bg-white" : "bg-brandblue-600";
  const titleTextColor = isInactive ? "text-gray-800" : "text-white";
  const subtitleTextColor = isInactive ? "text-gray-400" : "text-white";
  const accentTextColor = isInactive ? "text-brandblue-600" : "text-white";
  const borderColor = isEvaluationSession
    ? "border-4 border-brandblue-700"
    : null;

  return (
    <div
      className={classNames(
        `divide-y sm:divide-y-0 rounded-lg shadow-md p-4 flex gap-4 sm:justify-between flex-col sm:flex-row w-auto sm:w-full mx-auto sm:mx-0`,
        bgColor,
        titleTextColor,
        borderColor
      )}
    >
      {/* start reader info */}
      <div className="flex items-center gap-3 pe-4">
        <UserProfilePictureAvatar
          profilePictureUrl={profilePictureUrl}
          className="w-10 h-10"
          alt="sand-user-img"
        />

        <div className="flex items-start gap-1 flex-col">
          <h3 className={`text-sm font-medium`}>{participantName}</h3>
          <p className={`text-sm font-normal ${subtitleTextColor}`}>
            {telawahName}
          </p>
          <h3 className={`text-sm font-medium md:hidden`}>
            {sessionStatusText}
          </h3>
        </div>
      </div>
      {/* end reader info */}
      {!isEvaluationSession && (
        <div className="pe-4 hidden md:block">
          <div className="flex items-start gap-1 flex-col">
            <h3 data-cy="sessionStatusText" className={`text-sm font-medium`}>
              {sessionStatusText}
            </h3>
            <p className={`text-xs font-normal ${subtitleTextColor}`}>
              {`${t("readingProgress", {
                number: `${readingProgress}`,
              })}`}
            </p>
          </div>
        </div>
      )}
      {/* start session info */}
      <div className="pt-1 md:pt-0 md:pe-4 h-full   md:mt-2">
        <div className="flex items-center">
          <p className={"font-medium text-base"}>
            {t("sessionNum", { num: "# " + sessionNumber })}
          </p>
        </div>
      </div>
      {/* end session info */}
      {/* start session time */}
      <div
        className="pe-4 flex justify-center sm:static "
        data-cy="startSessionContainer"
      >
        <div className="flex md:items-start gap-4 md:gap-1 flex-row md:flex-col items-center">
          <p className={`text-xs font-normal ${subtitleTextColor}`}>
            {t("sessionTime")}
          </p>
          <h3 className={`text-xl font-semibold ${accentTextColor}`}>
            {sessionStartTime.format("hh:mm")}
            <span className="text-xs font-normal">
              {` ${sessionStartTime.format("A")}`}
            </span>
          </h3>
        </div>
      </div>
      {/* end session time */}
    </div>
  );
}
